import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { allVideos, categories } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '0', 10);
  const video = allVideos.find((v) => v.id === videoId);
  const category = categories.find((c) => c.slug === video?.category);

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6 lg:px-12">
          <div className="max-w-6xl mx-auto text-center py-20">
            <h2 className="text-2xl font-bold text-foreground-950 mb-4">内容未找到</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || video.category, href: `/category/${video.category}` },
    { label: video.title },
  ];

  const relatedVideos = allVideos
    .filter((v) => v.category === video.category && v.id !== video.id)
    .slice(0, 4);

  const categoryName = category?.name || video.category;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `一起草${categoryName} - ${video.title}`,
    description: video.description,
    datePublished: video.date,
    author: {
      '@type': 'Organization',
      name: '一起草在线观看',
    },
    publisher: {
      '@type': 'Organization',
      name: '一起草在线观看',
      url: 'https://wgecino.cn',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': item.href ? `https://wgecino.cn${item.href}` : `https://wgecino.cn/article/${video.id}`,
        name: item.label,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 lg:px-12">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <article className="py-6">
                {/* Video Player Area */}
                <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-6 bg-foreground-950">
                  <img
                    src={video.thumbnail}
                    alt={`一起草${categoryName} - ${video.title}`}
                    title={`一起草在线观看 - ${video.title}`}
                    className="w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 hover:bg-primary-500 flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer whitespace-nowrap">
                      <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
                    </button>
                  </div>
                  <span className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded-full font-medium">
                    {video.duration}
                  </span>
                </div>

                {/* Video Info */}
                <div className="mb-8">
                  <h1 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                    一起草{categoryName} - {video.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-eye-line"></i>
                      {video.views} 次观看
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-amber-400"></i>
                      {video.rating} 分
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i>
                      {video.date}
                    </span>
                    <Link
                      to={`/category/${video.category}`}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className={category?.icon || 'ri-folders-line'}></i>
                      {categoryName}
                    </Link>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {video.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100/70 rounded-lg p-5">
                    <h3 className="text-base font-semibold text-foreground-900 mb-3 font-heading">视频简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                  </div>
                </div>

                {/* Related Videos */}
                {relatedVideos.length > 0 && (
                  <section className="border-t border-background-200 pt-8">
                    <h3 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-6">
                      相关推荐
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      {relatedVideos.map((v) => (
                        <VideoCard key={v.id} {...v} />
                      ))}
                    </div>
                  </section>
                )}
              </article>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}